import { el, btn, backButton } from '@/ui/dom';
import { cssColor } from '@/ui/format';
import { citySilhouette } from '@/ui/citySilhouettes';
import { CITIES } from '@/config/cities';
import { sfxUiTick } from '@/audio/sfx';

/**
 * OnlineSetupScreen.ts — the host's setup for "Challenge Friends" (M33/M34;
 * M113 redesign onto the night-highway photo backdrop). One glass column
 * (left-anchored on desktop so the curving road stays visible on the right):
 * a BACK pill, the gold PRIVATE RACE kicker over the chrome/gold CHALLENGE
 * FRIENDS logotype, four wide OPTION ROWS (gold icon + label on the left,
 * big gold-when-selected pill choices on the right), the city photo grid, and
 * an oversized gold CREATE INVITE bar.
 *
 * The semantics are UNCHANGED from M87: a MODE toggle (Quick Race vs Tour) +
 * racer count, then either a city (quick race) or a tour length. Tour stops
 * are RANDOM by default; PICK CITIES turns the grid into an ordered
 * multi-select — tap cities in race order (numbered gold badges), any
 * unpicked stops fill randomly. Picking MORE cities than the chosen stop
 * count prompts to grow the tour to the next length option. Online has no
 * unlocks, so the grid is ungated.
 */
export interface OnlineSetupConfig {
  mode: 'race' | 'tour';
  /** Quick race: which city. */
  cityId: string;
  /** Total racers on the course (you + up to 6 friends; AI fills the rest). */
  racers: number;
  /** Tour: how many cities. */
  tourLen: number;
  /** Tour (M87): the host's ORDERED city picks ([] = fully random tour).
   *  Fewer picks than stops = the room fills the rest randomly. */
  tourCities: string[];
}

export interface OnlineSetupCallbacks {
  onCreate: (config: OnlineSetupConfig) => void;
  onBack: () => void;
}

const RACER_COUNTS = [2, 3, 4, 5, 6, 7];
const TOUR_LENGTHS = [3, 5, 9];
const MODES: { mode: 'race' | 'tour'; label: string }[] = [
  { mode: 'race', label: 'QUICK RACE' },
  { mode: 'tour', label: 'TOUR' },
];
const PICK_MODES: { mode: 'random' | 'pick'; label: string }[] = [
  { mode: 'random', label: 'RANDOM' },
  { mode: 'pick', label: 'PICK CITIES' },
];

/** Gold row glyphs (M113) — the game's inline-SVG icon voice (24-box,
 *  currentColor fills/strokes, secondary detail at reduced opacity). */
const ROW_ICONS = {
  /** Checkered race flag on its pole — MODE. */
  flag:
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M4.6 2.2c.55 0 1 .45 1 1V21a1 1 0 1 1-2 0V3.2c0-.55.45-1 1-1Z"/>' +
    '<path d="M7 4h3.3v3.3H7Zm6.6 0h3.3v3.3h-3.3Zm-3.3 3.3h3.3v3.4h-3.3Zm6.6 0H20v3.4h-3.1ZM7 10.7h3.3V14H7Zm6.6 0h3.3V14h-3.3Z"/>' +
    '<path d="M10.3 4h3.3v3.3h-3.3ZM16.9 4H20v3.3h-3.1ZM7 7.3h3.3v3.4H7Zm6.6 0h3.3v3.4h-3.3Zm-3.3 3.4h3.3V14h-3.3Zm6.6 0H20V14h-3.1Z" opacity="0.28"/></svg>',
  /** Racing helmet, visor punched out — RACERS. */
  helmet:
    '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path fill-rule="evenodd" d="M12 3.4c-5.5 0-9.2 4.2-9.2 9.3v3.4c0 1 .8 1.8 1.8 1.8h10.1c4 0 6.5-2.7 6.5-6.4 0-4.9-3.8-8.1-9.2-8.1Zm-1.2 6h7.6c1 0 1.8.8 1.8 1.8v.2c0 1-.8 1.8-1.8 1.8h-7.6c-1 0-1.8-.8-1.8-1.8v-.2c0-1 .8-1.8 1.8-1.8Z"/></svg>',
  /** Stopwatch — STOPS. */
  watch:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
    '<circle cx="12" cy="13.6" r="7.2"/><path d="M12 9.6v4l2.9 1.7"/>' +
    '<path d="M10 2.6h4M12 2.6v3" stroke-linecap="round"/>' +
    '<path d="M18.4 6.6l1.5 1.5" stroke-linecap="round"/></svg>',
  /** City towers with lit windows — CITIES. */
  city:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">' +
    '<path d="M3.4 20.6V10.2h5.2v10.4M8.6 20.6V4.8h6.8v15.8M15.4 20.6v-8h5.2v8M2 20.6h20"/>' +
    '<path d="M6 13v.01M6 15.7v.01M6 18.3v.01M11.1 7.8v.01M13.9 7.8v.01M11.1 10.6v.01M13.9 10.6v.01M11.1 13.4v.01M13.9 13.4v.01M11.1 16.2v.01M13.9 16.2v.01M18 15.2v.01M18 17.9v.01" stroke-width="1.7" stroke-linecap="round"/></svg>',
} as const;

export class OnlineSetupScreen {
  private root: HTMLDivElement;
  private modeChips: { mode: 'race' | 'tour'; el: HTMLButtonElement }[] = [];
  private racerChips: HTMLButtonElement[] = [];
  private tourChips: HTMLButtonElement[] = [];
  private pickChips: { mode: 'random' | 'pick'; el: HTMLButtonElement }[] = [];
  private cityCards: { id: string; card: HTMLButtonElement; pickBadge: HTMLSpanElement }[] = [];
  private tourRow: HTMLDivElement;
  private pickRow: HTMLDivElement;
  private pickLabel: HTMLSpanElement;
  private cityGrid: HTMLDivElement;
  private sub: HTMLDivElement;
  private prompt: HTMLDivElement;
  private cfg: OnlineSetupConfig;
  /** Tour stops chooser (M87): 'random' rolls all stops; 'pick' opens the grid. */
  private pickMode: 'random' | 'pick' = 'random';
  /** The over-pick city waiting on the "more stops?" prompt's answer. */
  private pendingPick: string | null = null;

  constructor(
    parent: HTMLElement,
    private cb: OnlineSetupCallbacks,
    defaults: OnlineSetupConfig,
  ) {
    this.cfg = { ...defaults, tourCities: [...(defaults.tourCities ?? [])] };

    this.root = el('div', 'screen screen-onlinesetup screen-onlinebg', parent);
    this.root.style.display = 'none';

    // The one glass column over the photo (left-anchored on desktop).
    const panel = el('div', 'osu-panel', this.root);

    // M114b — BACK is the ONE shared chevron disc now (was a pill reading
    // "‹ BACK" in an `.osu-top` row inside the panel). It pins to the SCREEN's
    // upper-left so it matches every other menu, and the panel's own top row
    // is gone with it.
    backButton(this.root, () => this.cb.onBack());

    // Editorial head: gold kicker + short rule, chrome/gold logotype, italic sub.
    const head = el('div', 'osu-head', panel);
    el('div', 'osu-kicker', head).textContent = 'PRIVATE RACE';
    el('div', 'osu-kickrule', head);
    const title = el('div', 'osu-title', head);
    title.innerHTML =
      '<span class="osu-title-chrome" data-text="CHALLENGE">CHALLENGE</span> ' +
      '<span class="osu-title-gold" data-text="FRIENDS">FRIENDS</span>';
    this.sub = el('div', 'osu-sub', head);

    const rows = el('div', 'osu-rows', panel);

    // MODE — quick race vs tour.
    const modeRow = this.optionRow(rows, ROW_ICONS.flag, 'MODE');
    for (const m of MODES) {
      const pill = btn(m.label, 'btn osu-pill', modeRow.opts, () => this.setMode(m.mode));
      this.modeChips.push({ mode: m.mode, el: pill });
    }

    // RACERS — total field 2..7 (AI fills unclaimed seats).
    const racerRow = this.optionRow(rows, ROW_ICONS.helmet, 'RACERS');
    for (const n of RACER_COUNTS) {
      const pill = btn(String(n), 'btn osu-pill osu-pill-num', racerRow.opts, () => {
        this.cfg.racers = n;
        this.refresh();
      });
      this.racerChips.push(pill);
    }

    // STOPS — tour length (shown only in TOUR mode).
    const stopsRow = this.optionRow(rows, ROW_ICONS.watch, 'STOPS');
    this.tourRow = stopsRow.row;
    for (const n of TOUR_LENGTHS) {
      const pill = btn(String(n), 'btn osu-pill osu-pill-num', stopsRow.opts, () => {
        this.cfg.tourLen = n;
        // Shrinking the tour below the picks keeps the FIRST n picks.
        if (this.cfg.tourCities.length > n) this.cfg.tourCities.length = n;
        this.refresh();
      });
      this.tourChips.push(pill);
    }

    // CITIES — random stops, or hand-pick them on the grid (M87).
    const citiesRow = this.optionRow(rows, ROW_ICONS.city, 'CITIES');
    this.pickRow = citiesRow.row;
    this.pickLabel = citiesRow.label;
    for (const p of PICK_MODES) {
      const pill = btn(p.label, 'btn osu-pill', citiesRow.opts, () => {
        if (this.pickMode === p.mode) return;
        sfxUiTick();
        this.pickMode = p.mode;
        if (p.mode === 'random') this.cfg.tourCities = []; // back to a full roll
        this.refresh();
      });
      this.pickChips.push({ mode: p.mode, el: pill });
    }

    // The city grid: quick-race single-select, or the tour's ordered picker.
    this.cityGrid = el('div', 'osu-grid', panel);
    for (const city of CITIES) {
      const card = el('button', 'city-card', this.cityGrid);
      card.type = 'button';
      card.style.setProperty('--card-accent', cssColor(city.theme.roadLine));
      const art = el('div', 'city-card-art', card);
      art.innerHTML = `<span class="city-card-sil">${citySilhouette(city.id)}</span>`;
      const photo = document.createElement('img');
      photo.className = 'city-card-photo';
      photo.alt = '';
      photo.addEventListener('error', () => photo.remove());
      photo.src = `${import.meta.env.BASE_URL}cities/${city.id}.webp`;
      art.appendChild(photo);
      const pickBadge = el('span', 'city-card-picknum', card);
      el('div', 'city-card-name', card).textContent = city.name;
      el('div', 'city-card-country', card).textContent = city.country;
      card.addEventListener('click', () => this.onCityTap(city.id));
      this.cityCards.push({ id: city.id, card, pickBadge });
    }

    // CREATE INVITE — the oversized gold bar, sticky at the panel's bottom so
    // the primary action never leaves the screen while the grid scrolls.
    const createWrap = el('div', 'osu-createwrap', panel);
    const create = btn('', 'btn btn-primary osu-create', createWrap, () =>
      this.cb.onCreate({ ...this.cfg, tourCities: [...this.cfg.tourCities] }),
    );
    create.innerHTML =
      '<span class="osu-create-ch" aria-hidden="true">›››</span>' +
      '<span class="osu-create-label">CREATE INVITE</span>' +
      '<span class="osu-create-ch" aria-hidden="true">›››</span>';

    // M87: the "grow the tour?" prompt (an in-screen dialog, built once).
    this.prompt = el('div', 'osetup-prompt', this.root);
    this.prompt.style.display = 'none';
  }

  /** One wide option bar: gold icon + letterspaced label left, pills right. */
  private optionRow(
    parent: HTMLElement,
    icon: string,
    labelText: string,
  ): { row: HTMLDivElement; label: HTMLSpanElement; opts: HTMLDivElement } {
    const row = el('div', 'osu-row', parent);
    const head = el('div', 'osu-row-head', row);
    const ic = el('span', 'osu-row-ic', head);
    ic.innerHTML = icon;
    const label = el('span', 'osu-row-label', head);
    label.textContent = labelText;
    const opts = el('div', 'osu-opts', row);
    return { row, label, opts };
  }

  private setMode(mode: 'race' | 'tour'): void {
    this.cfg.mode = mode;
    this.refresh();
  }

  private onCityTap(cityId: string): void {
    if (this.cfg.mode === 'race') {
      this.cfg.cityId = cityId;
      this.refresh();
      return;
    }
    // Tour PICK mode: ordered toggle. (The grid is hidden in RANDOM mode.)
    const picks = this.cfg.tourCities;
    const at = picks.indexOf(cityId);
    if (at >= 0) {
      picks.splice(at, 1); // un-pick — later numbers shuffle down
      this.refresh();
      return;
    }
    if (picks.length < this.cfg.tourLen) {
      picks.push(cityId);
      this.refresh();
      return;
    }
    // Over-pick: every stop is taken — offer the longer tour options.
    this.pendingPick = cityId;
    this.showPrompt();
  }

  /** "That's more cities than your stops — grow the tour?" (owner spec). */
  private showPrompt(): void {
    const higher = TOUR_LENGTHS.filter((n) => n > this.cfg.tourLen);
    if (higher.length === 0 || this.pendingPick === null) {
      this.pendingPick = null;
      return; // already at the max — nothing to offer
    }
    this.prompt.replaceChildren();
    const card = el('div', 'osetup-prompt-card', this.prompt);
    el('div', 'osetup-prompt-kicker', card).textContent = 'TOUR IS FULL';
    el('div', 'osetup-prompt-text', card).textContent =
      `You picked all ${this.cfg.tourLen} stops. Add more stops to fit another city?`;
    const row = el('div', 'osetup-prompt-row', card);
    for (const n of higher) {
      btn(`MAKE IT ${n} STOPS`, 'btn btn-primary osetup-prompt-btn', row, () => {
        sfxUiTick();
        this.cfg.tourLen = n;
        if (this.pendingPick && !this.cfg.tourCities.includes(this.pendingPick)) {
          this.cfg.tourCities.push(this.pendingPick);
        }
        this.pendingPick = null;
        this.hidePrompt();
        this.refresh();
      });
    }
    btn(`KEEP ${this.cfg.tourLen} STOPS`, 'btn btn-ghost osetup-prompt-btn', row, () => {
      sfxUiTick();
      this.pendingPick = null;
      this.hidePrompt();
    });
    this.prompt.style.display = '';
  }

  private hidePrompt(): void {
    this.prompt.style.display = 'none';
  }

  private refresh(): void {
    const tour = this.cfg.mode === 'tour';
    const picking = tour && this.pickMode === 'pick';
    const picks = this.cfg.tourCities;
    this.sub.textContent = tour
      ? picking
        ? 'Tap cities in race order. Any unpicked stops are drawn at random.'
        : 'A championship across a random mix of cities. Share the invite link.'
      : 'Pick a city and how many racers, then share the invite link.';
    this.modeChips.forEach((m) => m.el.classList.toggle('on', m.mode === this.cfg.mode));
    this.racerChips.forEach((c, i) =>
      c.classList.toggle('on', RACER_COUNTS[i] === this.cfg.racers),
    );
    this.tourChips.forEach((c, i) =>
      c.classList.toggle('on', TOUR_LENGTHS[i] === this.cfg.tourLen),
    );
    this.pickChips.forEach((p) => p.el.classList.toggle('on', p.mode === this.pickMode));
    this.pickLabel.textContent = picking
      ? `CITIES · ${picks.length}/${this.cfg.tourLen} PICKED${picks.length < this.cfg.tourLen ? ' · REST RANDOM' : ''}`
      : 'CITIES';
    this.cityCards.forEach(({ id, card, pickBadge }) => {
      const pickedAt = picking ? picks.indexOf(id) : -1;
      card.classList.toggle('selected', !tour && id === this.cfg.cityId);
      card.classList.toggle('picked', pickedAt >= 0);
      pickBadge.textContent = pickedAt >= 0 ? String(pickedAt + 1) : '';
    });
    this.tourRow.style.display = tour ? '' : 'none';
    this.pickRow.style.display = tour ? '' : 'none';
    this.cityGrid.style.display = tour && !picking ? 'none' : '';
  }

  show(defaults?: OnlineSetupConfig): void {
    if (defaults) {
      this.cfg = { ...defaults, tourCities: [...(defaults.tourCities ?? [])] };
      this.pickMode = this.cfg.tourCities.length > 0 ? 'pick' : 'random';
    }
    this.pendingPick = null;
    this.hidePrompt();
    this.refresh();
    this.root.scrollTop = 0;
    this.root.style.display = '';
  }

  hide(): void {
    this.hidePrompt();
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.root.remove();
  }
}
